import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

// import Plotly from 'plotly.js-dist-min';

// Configuración global de axios para incluir credenciales
axios.defaults.withCredentials = true;
const primary = '#c45151ff';

const ComponentSelectorModal: React.FC<{
    onClose: () => void;
    onSelect: (element: any) => void;
}> = ({ onClose, onSelect }) => {
    const [categories, setCategories] = useState<string[]>([]);
    const [subCategories, setSubCategories] = useState<string[]>([]);
    const [elements, setElements] = useState<{ id: number; name: string }[]>([]);

    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(null);
    const [selectedElement, setSelectedElement] = useState<{ id: number; name: string } | null>(null);
    const [componentInfo, setComponentInfo] = useState<{ id: number; name: string ; content : string} | null>(null);
    const [selected, setSelected] = useState<any>(null);
    const [iframeSrcDoc, setIframeSrcDoc] = useState<string | null>(null);


    const markdownToSrcDoc = (md: string): string => {
        const html = marked.parse(md || '');
        // const safe = DOMPurify.sanitize(html, {
        //     // permitir data: (para data:image/...), http(s) y mailto
        //     ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|data:)/i
        // });
        const safe = html;
        return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial; padding:12px; color:#111;}</style></head><body>${safe}</body></html>`;
    };

    useEffect(() => {
        let mounted = true;
        let blobUrl: string | null = null;
        if (selected) {
            // try {
            //     const doc = markdownToSrcDoc(String(selected));
            //     const blob = new Blob([doc], { type: 'text/html' });
            //     blobUrl = URL.createObjectURL(blob);
            //     if (mounted) setIframeSrcDoc(blobUrl);
            // } catch (e) {
            //     console.error('Error converting markdown to srcDoc:', e);
            //     if (mounted) setIframeSrcDoc(null);
            // }
            try {
                const doc = markdownToSrcDoc(String(selected));
                if (mounted) setIframeSrcDoc(doc);
            } catch (e) {
                console.error('Error converting markdown to srcDoc:', e);
                if (mounted) setIframeSrcDoc(null);
            }
        } else {
            setIframeSrcDoc(null);
        }
        return () => {
            mounted = false;
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
    }, [selected]);

    // Fetch categories on mount
    useEffect(() => {
        axios.post(`${config.API_BASE_URL}/components/get-unique-categories.php`)
            .then(response => {
                if (response.data.success) {
                    const sortedCategories = response.data.categories.sort((a: string, b: string) => a.localeCompare(b));
                    setCategories(sortedCategories);
                }
            })
            .catch(error => console.error('Error fetching categories:', error));
    }, []);

    // Fetch subcategories when a category is selected
    useEffect(() => {
        if (selectedCategory) {
            axios.post(`${config.API_BASE_URL}/components/get-subcategories-by-category.php`, { category: selectedCategory })
                .then(response => {
                    if (response.data.success) {
                        const sortedSubCategories = response.data.sub_categories.sort((a: string, b: string) => a.localeCompare(b));
                        setSubCategories(sortedSubCategories);
                        setElements([]); // Clear elements when category changes
                        setSelectedSubCategory(null);
                        setSelected(null); // Clear the selected component
                        setComponentInfo(null);
                    }
                })
                .catch(error => console.error('Error fetching subcategories:', error));
        }
    }, [selectedCategory]);

    // Fetch elements when a subcategory is selected
    useEffect(() => {
        if (selectedCategory && selectedSubCategory) {
            console.log('Selected Category:', selectedCategory);
            axios.post(`${config.API_BASE_URL}/components/get-elements-by-category-and-subcategory.php`, {
                category: selectedCategory,
                sub_category: selectedSubCategory
            })
                .then(response => {
                    if (response.data.success) {
                        console.log('Response:');
                        const sortedElements = response.data.elements.sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name));
                        setElements(sortedElements);
                        setSelected(null); // Clear the selected component
                        setSelectedElement(null);
                        setComponentInfo(null);
                    }
                })
                .catch(error => console.error('Error fetching elements:', error));
        }
    }, [selectedCategory, selectedSubCategory]);

    const handleElementClick = (element: { id: number; name: string }) => {
        setSelectedElement(element);
        axios.post(`${config.API_BASE_URL}/components/get-component.php`, { id: element.id })
            .then(response => {
                if (response.data.success) {
                    console.log('Component data:', response.data.data.component);
                    setComponentInfo({ id: element.id, name: element.name, content: response.data.data.component });
                    setSelected(response.data.data.description);
                }
            })
            .catch(error => console.error('Error fetching component and display_table:', error));
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
            <div style={{
                background: '#f9f9f9', width: 860, maxWidth: '96%', height: 560, borderRadius: 12, display: 'flex', flexDirection: 'column',
                boxShadow: '0 14px 40px rgba(13,21,28,0.24)', overflow: 'hidden', fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial'
            }}>
                <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                    <div style={{ width: 360, padding: 20, display: 'flex', flexDirection: 'column', gap: 18, borderRight: '1px solid #f0f0f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <label style={{ color: '#666', fontSize: 13, minWidth: 70 }}>Library</label>
                            <select
                                value={selectedCategory || ''}
                                onChange={e => setSelectedCategory(e.target.value)}
                                style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid #e9e9e9', background: '#fff', fontSize: 14 }}
                            >
                                <option value="" disabled>Please select a category</option>
                                {categories.map(category => (
                                    <option key={category} value={category}>{category}</option>
                                ))}
                            </select>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ color: '#666', fontSize: 13 }}>Category</div>
                            <ul style={{ listStyle: 'none', padding: 6, margin: 0, maxHeight: 150, minHeight: 150, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 , background: '#ffffff'}}>
                                {subCategories.map(subCategory => (
                                    <li
                                        key={subCategory}
                                        style={{
                                            padding: '3px 12px',
                                            cursor: 'pointer',
                                            borderRadius: 8,
                                            border: selectedSubCategory === subCategory ? `1px solid ${primary}` : '1px solid transparent',
                                            background: selectedSubCategory === subCategory ? 'rgba(141,57,57,0.06)' : 'transparent',
                                            color: '#222',
                                            fontSize: 14
                                        }}
                                        onClick={() => setSelectedSubCategory(subCategory)}
                                    >
                                        {subCategory}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ color: '#666', fontSize: 13 }}>Elements</div>
                            <ul style={{ listStyle: 'none', padding: 6, margin: 0, maxHeight: 150, minHeight: 150, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6,  background: '#ffffff'}}>
                                {elements.map(element => (
                                    <li
                                        key={element.id}
                                        style={{
                                            padding: '3px 12px',
                                            cursor: 'pointer',
                                            borderRadius: 8,
                                            display: 'flex', alignItems: 'center',
                                            justifyContent: 'space-between',
                                            background: selectedElement?.id === element.id ? 'rgba(141,57,57,0.06)' : 'transparent',
                                            border: selectedElement?.id === element.id ? `1px solid ${primary}` : '1px solid transparent',
                                            color: '#222',
                                            fontSize: 14
                                        }}
                                        onClick={() => handleElementClick(element)}
                                    >
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{element.name}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div style={{ flex: 1, padding: 10, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ flex: 1, borderRadius: 10, background: '#fbfbfb', padding: 12, border: '1px solid #f0f0f0', overflow: 'hidden' }}>
                            {iframeSrcDoc ? (
                                <iframe
                                    srcDoc={iframeSrcDoc}
                                    style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8 }}
                                    title="Component Info"
                                />
                            ) : (
                                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: 15, marginBottom: 6, color: '#444' }}>Select a component</div>
                                        <div style={{ fontSize: 13 }}>Choose an element on the left to view details.</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div style={{ borderTop: '1px solid #f3f3f3', padding: 5, display: 'flex', justifyContent: 'flex-end', gap: 12, background: '#fff' }}>
                    <button onClick={onClose} style={{ background: 'transparent', color: '#666', border: '1px solid #e9e9e9', padding: '8px 12px', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
                    <button
                        onClick={() => componentInfo && onSelect(componentInfo)}
                        disabled={!componentInfo}
                        style={{ background: componentInfo ? primary : '#ddd', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: componentInfo ? 'pointer' : 'not-allowed' }}
                    >
                        Accept
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ComponentSelectorModal;